import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Container } from '@vnetwork/ui/layout';
import { DetailHeader } from '@/components/detail-header';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { useRack, type Rack } from '@/api/capacities';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { formatWhDateTime } from '../shared/wh-format';
import { rackStatusView } from './rack-mappers';
import { RackLayoutTab } from './RackLayoutTab';
import { RackItemsTab } from './RackItemsTab';
import { RackActivityTab } from './RackActivityTab';

/** "Rack Information" card rows, matched to the legacy render (empty fields hidden). */
function rackInfoItems(rack: Rack): InfoItem[] {
  const x = rack as Rack & {
    started_at?: string;
    cancelled_at?: string;
    notes?: string;
    created_at?: string;
    updated_at?: string;
  };
  const rows: InfoItem[] = [
    { label: 'Rack Code', value: rack.code || '—' },
    { label: 'Rack Name', value: rack.name || '—' },
  ];
  if (rack.location?.name) rows.push({ label: 'Location', value: rack.location.name });
  rows.push({ label: 'Status', value: <StatusPill tone={rackStatusView(rack.status).tone}>{rackStatusView(rack.status).label}</StatusPill> });
  rows.push({ label: 'U Height', value: rack.u_height ? `${rack.u_height}U` : '—' });
  if (rack.power_supply) rows.push({ label: 'Power Supply', value: rack.power_supply });
  if (rack.bandwidth_limit != null) rows.push({ label: 'Bandwidth Limit', value: String(rack.bandwidth_limit) });
  if (rack.bandwidth_reality != null) rows.push({ label: 'Bandwidth Usage', value: String(rack.bandwidth_reality) });
  if (x.started_at) rows.push({ label: 'Started At', value: formatWhDateTime(x.started_at) });
  if (x.cancelled_at) rows.push({ label: 'Cancelled At', value: formatWhDateTime(x.cancelled_at) });
  if (rack.description) rows.push({ label: 'Description', value: rack.description, full: true });
  if (x.notes) rows.push({ label: 'Notes', value: x.notes, full: true });
  rows.push({ label: 'Created At', value: formatWhDateTime(x.created_at) });
  rows.push({ label: 'Updated At', value: formatWhDateTime(x.updated_at) });
  return rows;
}

export interface CapacityRackDetailPageProps {
  /** Rack id. Falls back to the `:id` route param when omitted. */
  id?: string;
}

/**
 * Rack detail — identity header (`#code` + name + status pill) over two tabs:
 * Layout (info card + @ui `RackLayoutView` of mounted items) and Activity
 * (`useEntityLogs('racks', id)` Timeline). Read-only (W3 scope).
 */
export default function CapacityRackDetailPage({ id: idProp }: CapacityRackDetailPageProps) {
  const params = useParams();
  const navigate = useNavigate();
  const id = idProp ?? params.id ?? '';
  const [tab, setTab] = useState('equipment');

  const { data: rack, isLoading, isError, refetch } = useRack(id);

  const status = rackStatusView(rack?.status);

  return (
    <Container size="full">
      {isError ? (
        <TabError title="Failed to load rack" onRetry={() => refetch()} />
      ) : isLoading || !rack ? (
        <TabLoading rows={4} />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <DetailHeader
            parent={[
              { label: 'Capacities & Resources', to: '/capacities' },
              { label: 'Racks', to: '/capacities/racks' },
            ]}
            current={rack.name || rack.code}
            idPrefix={rack.code ? `#${rack.code}` : undefined}
            title={rack.name}
            meta={<StatusPill tone={status.tone}>{status.label}</StatusPill>}
            actions={
              <Button
                variant="secondary"
                startIcon={<Pencil size={15} />}
                onClick={() => navigate(`/capacities/racks/${rack.id}/edit`)}
              >
                Edit
              </Button>
            }
          />

          <Card flat>
            <CardHeader>
              <CardTitle>Rack Information</CardTitle>
            </CardHeader>
            <CardBody>
              <InfoGrid items={rackInfoItems(rack)} />
            </CardBody>
          </Card>

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="equipment">Equipment in Rack</TabsTrigger>
              <TabsTrigger value="components">Components in Rack</TabsTrigger>
              <TabsTrigger value="layout">Rack Layout</TabsTrigger>
              <TabsTrigger value="activity">Activity Logs</TabsTrigger>
            </TabsList>

            <TabsContent value="equipment">
              <RackItemsTab rackId={rack.id} itemType="equipment" />
            </TabsContent>

            <TabsContent value="components">
              <RackItemsTab rackId={rack.id} itemType="component" />
            </TabsContent>

            <TabsContent value="layout">
              <RackLayoutTab rack={rack} />
            </TabsContent>

            <TabsContent value="activity">
              <RackActivityTab rackId={rack.id} />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Container>
  );
}
